/**
 * Requirements Smart Categorization Functions
 * Extracted from FullRequirementsReview.js to reduce file size
 */

import { 
  Package, AlertCircle, FileText, Book, Award, Shield, 
  CheckCircle, XCircle, AlertTriangle, Download, Eye,
  Users, Wrench, Activity, Clipboard, Database
} from 'lucide-react';

// Smart categorization functions
export const isCrewRelated = (item) => {
  const crewKeywords = ['crew', 'manning', 'master', 'officer', 'stcw', 'coc', 'medical fitness', 'radio operator', 'familiarization', 'mlc', 'maritime labour', 'hours of rest', 'skipper', 'deck officer', 'engineer officer', 'bosun', 'deckhand', 'seafarer'];
  const itemText = `${item.name || ''} ${item.category || ''} ${item.description || ''} ${item.id || ''}`.toLowerCase();
  return crewKeywords.some(keyword => itemText.includes(keyword)) ||
         (item.category && (item.category.includes('Master') || item.category.includes('Second Person') || item.category.includes('Manning') || item.category.includes('Medical') || item.category.includes('Training Certificates') || item.category.includes('Radio Certificates') || item.category.includes('Maritime Labour')));
};

export const isEquipmentRelated = (item) => {
  const itemText = `${item.name || ''} ${item.category || ''} ${item.description || ''}`.toLowerCase();
  
  // EXCLUDE training, procedures, familiarization, risk assessments first
  const excludeKeywords = [
    'training', 'course', 'familiarisation', 'familiarization', 'instruction', 'drill', 'exercise', 
    'procedure', 'checklist', 'protocol', 'guideline', 'plan', 'response',
    'risk assessment', 'hazard', 'safety evaluation', 'risk analysis',
    'certificate', 'survey', 'inspection', 'documentation', 'record'
  ];
  
  if (excludeKeywords.some(keyword => itemText.includes(keyword))) {
    return false;
  }
  
  // Only include actual physical equipment and systems
  const equipmentKeywords = [
    'extinguisher', 'detector', 'alarm', 'pump', 'engine', 'generator', 'battery',
    'lifebuoy', 'lifejacket', 'liferaft', 'radar', 'gps', 'radio', 'compass', 'ais', 'epirb',
    'anchor', 'winch', 'davit', 'crane', 'hoist', 'valve', 'tank', 'cylinder',
    'lamp', 'light', 'searchlight', 'horn', 'whistle', 'bell', 'gong',
    'cable', 'wire', 'fuse', 'switch', 'panel', 'motor', 'compressor'
  ];
  
  const hasEquipmentKeyword = equipmentKeywords.some(keyword => itemText.includes(keyword));
  
  // Check category but be more restrictive
  const hasEquipmentCategory = item.category && (
    item.category.includes('FFE') || 
    item.category.includes('LSA') ||
    (item.category.includes('Equipment') && !item.category.includes('Training') && !item.category.includes('Documentation')) ||
    item.category.includes('Navigation Equipment') ||
    item.category.includes('GMDSS Equipment') ||
    item.category.includes('Electrical Equipment') ||
    item.category.includes('Machinery Equipment')
  );
  
  return hasEquipmentKeyword || hasEquipmentCategory;
};

export const isTrainingRelated = (item) => {
  const trainingKeywords = [
    'training', 'course', 'competency', 'qualification', 'certification', 'instruction', 
    'drill', 'exercise', 'assessment', 'education', 'familiarisation', 'familiarization',
    'pyrotechnic familiarisation', 'lsa familiarisation', 'engine familiarisation'
  ];
  const itemText = `${item.name || ''} ${item.category || ''} ${item.description || ''}`.toLowerCase();
  return trainingKeywords.some(keyword => itemText.includes(keyword)) ||
         (item.category && (
           item.category.includes('Training') || 
           item.category.includes('Familiarization') || 
           item.category.includes('Familiarisation') ||
           item.category.includes('Drill') ||
           item.category.includes('Training Certificates')
         )) ||
         (item.name && (
           item.name.toLowerCase().includes('drill') ||
           item.name.toLowerCase().includes('familiarisation') ||
           item.name.toLowerCase().includes('familiarization')
         ));
};

export const isProcedureRelated = (item) => {
  const procedureKeywords = ['procedure', 'checklist', 'protocol', 'instruction', 'guideline', 'plan', 'response', 'emergency', 'operation'];
  const itemText = `${item.name || ''} ${item.category || ''} ${item.description || ''}`.toLowerCase();
  return procedureKeywords.some(keyword => itemText.includes(keyword)) ||
         (item.category && (item.category.includes('Procedure') || item.category.includes('Emergency') || item.category.includes('Operation')));
};

export const isVesselDocumentationRelated = (item) => {
  const vesselDocKeywords = ['certificate', 'survey', 'inspection', 'registration', 'insurance', 'compliance', 'examination', 'audit', 'assessment'];
  const itemText = `${item.name || ''} ${item.category || ''} ${item.description || ''}`.toLowerCase();
  return (vesselDocKeywords.some(keyword => itemText.includes(keyword)) &&
          !isCrewRelated(item) && !isTrainingRelated(item)) ||
         (item.category && (item.category.includes('Survey') || item.category.includes('Certificate') || item.category.includes('Compliance')) && !isCrewRelated(item));
};

export const isRiskAssessmentRelated = (item) => {
  const riskKeywords = [
    'risk assessment', 'hazard', 'safety evaluation', 'risk analysis', 'safety assessment',
    'risk management', 'hazard identification', 'risk control', 'safety analysis'
  ];
  const itemText = `${item.name || ''} ${item.category || ''} ${item.description || ''}`.toLowerCase();
  return riskKeywords.some(keyword => itemText.includes(keyword)) ||
         (item.category && (
           item.category.includes('Risk') ||
           item.category.includes('Safety Assessment') ||
           item.category.includes('Hazard')
         )) ||
         (item.name && item.name.toLowerCase().includes('risk'));
};

// Enhanced categorization system with smart filtering
export const REQUIREMENT_CATEGORIES = {
  crew: {
    title: '👥 Crew Requirements',
    icon: Users,
    color: 'teal',
    fields: ['crewCertificationByArea'],
    smartFilter: isCrewRelated,
    description: 'All crew qualifications, certifications, manning, and competency requirements'
  },
  equipment: {
    title: '🔧 Equipment & Systems',
    icon: Package,
    color: 'blue',
    fields: ['basicEquipment', 'equipment', 'areaCategoryEquipment'],
    smartFilter: isEquipmentRelated,
    description: 'Physical equipment, systems, and safety appliances required for the vessel'
  },
  training: {
    title: '🎓 Training & Competency',
    icon: Book, 
    color: 'yellow',
    fields: ['training'],
    smartFilter: isTrainingRelated,
    description: 'Training courses, drills, and competency development requirements'
  },
  procedures: {
    title: '📋 Procedures & Operations',
    icon: Clipboard,
    color: 'green', 
    fields: ['procedures'],
    smartFilter: isProcedureRelated,
    description: 'Operational procedures, emergency response, and operational protocols'
  },
  vesselDocumentation: {
    title: '📄 Vessel Documentation',
    icon: FileText,
    color: 'gray',
    fields: ['basicDocumentation', 'documentation', 'basicCertificates', 'certificates'],
    smartFilter: isVesselDocumentationRelated,
    description: 'Vessel-specific documents, surveys, certificates, and regulatory compliance'
  },
  riskAssessments: {
    title: '⚠️ Risk Assessments',
    icon: AlertTriangle,
    color: 'red',
    fields: ['riskAssessments'],
    smartFilter: isRiskAssessmentRelated,
    description: 'Required risk assessments and safety evaluations'
  },
  restrictions: {
    title: '🚫 Operational Restrictions',
    icon: XCircle,
    color: 'purple',
    fields: ['restrictions'],
    smartFilter: (item) => item.category && item.category.toLowerCase().includes('restriction'),
    description: 'Operational limitations and regulatory restrictions'
  }
};